import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ConsumableWarehouseReportModal from '../components/ConsumableWarehouseReportModal';
import { fetchConsumableTransfers } from '../lib/consumableTransferHelper';
import { supabase } from '../lib/supabaseClient';

// Fallback image parser if prop not provided or invalid
const defaultParseImages = (imageUrlField) => {
  if (!imageUrlField) return { full: '', zoom: '', other: '', document: '' };
  if (typeof imageUrlField === 'object') {
    return {
      full: imageUrlField.full || '',
      zoom: imageUrlField.zoom || '',
      other: imageUrlField.other || '',
      document: imageUrlField.document || ''
    };
  }
  if (typeof imageUrlField !== 'string') return { full: '', zoom: '', other: '', document: '' };

  if (imageUrlField.startsWith('{') && imageUrlField.endsWith('}')) {
    try {
      const parsed = JSON.parse(imageUrlField);
      return {
        full: parsed.full || '',
        zoom: parsed.zoom || '',
        other: parsed.other || '',
        document: parsed.document || ''
      };
    } catch (e) {
      return { full: imageUrlField, zoom: '', other: '', document: '' };
    }
  }
  return { full: imageUrlField, zoom: '', other: '', document: '' };
};

// Fallback getStatusConfig
const defaultGetStatusConfig = (lotAmount, expDate) => {
  if (lotAmount <= 0) return { bg: '#795548', text: '#fff', label: 'หมด' };
  if (!expDate || expDate === '9999-12-31') return { bg: '#4caf50', text: '#fff', label: 'ปกติ' };
  const today = new Date();
  const expire = new Date(expDate);
  const diffMonths = (expire - today) / (1000 * 60 * 60 * 24 * 30);
  if (expire <= today) return { bg: '#000', text: '#fff', label: 'หมดอายุ' };
  if (diffMonths < 3) return { bg: '#ff4d4d', text: '#fff', label: '< 3ด.' };
  if (diffMonths <= 12) return { bg: '#ff9800', text: '#fff', label: '3ด.-1ปี' };
  return { bg: '#4caf50', text: '#fff', label: 'ปกติ' };
};

// 4 Primary Warehouses configuration
const WAREHOUSE_CONFIGS = [
  {
    key: 'main',
    name: 'คลังใหญ่ (คลังพัสดุ)',
    shortName: 'คลังใหญ่',
    icon: '🏢',
    accentColor: '#00bcd4',
    bgLight: 'rgba(0, 188, 212, 0.12)',
    borderLight: 'rgba(0, 188, 212, 0.3)'
  },
  {
    key: 'sub',
    name: 'คลังย่อย (คลังห้องฟัน)',
    shortName: 'คลังย่อย',
    icon: '🏬',
    accentColor: '#3b82f6',
    bgLight: 'rgba(59, 130, 246, 0.12)',
    borderLight: 'rgba(59, 130, 246, 0.3)'
  },
  {
    key: 'storage',
    name: 'ห้องเก็บของ',
    shortName: 'ห้องเก็บของ',
    icon: '📦',
    accentColor: '#f59e0b',
    bgLight: 'rgba(245, 158, 11, 0.12)',
    borderLight: 'rgba(245, 158, 11, 0.3)'
  },
  {
    key: 'clinic',
    name: 'ห้องคลีนิก',
    shortName: 'ห้องคลีนิก',
    icon: '🏥',
    accentColor: '#10b981',
    bgLight: 'rgba(16, 185, 129, 0.12)',
    borderLight: 'rgba(16, 185, 129, 0.3)'
  }
];

// Helper to determine warehouse key
const getWarehouseKey = (whName) => {
  if (!whName || typeof whName !== 'string') return null;
  if (whName.includes('คลังใหญ่')) return 'main';
  if (whName.includes('คลังย่อย')) return 'sub';
  if (whName.includes('ห้องเก็บของ')) return 'storage';
  if (whName.includes('ห้องคลีนิก')) return 'clinic';
  return null;
};

// Helper: Extract actor from log entry
const getLogActor = (log) => {
  if (log.user) return log.user;
  if (log.actor) return log.actor;
  if (log.created_by) return log.created_by;
  if (log.action && typeof log.action === 'string') {
    const match = log.action.match(/โดย\s+([^\s,()]+)/);
    if (match) return match[1];
  }
  return '-';
};

// Helper: Thai Locale Timestamp Formatter
const formatThaiTimestamp = (ts) => {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return String(ts);
    return d.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return String(ts);
  }
};

const ConsumableDashboardPage = ({
  itemMaster = [],
  supplies = [],
  logs = [],
  onNavigate = () => {},
  onOpenTransfer = () => {},
  getStatusConfig = defaultGetStatusConfig,
  parseImages = defaultParseImages,
  setExpandedImage = () => {},
  user = {},
  isMobile = false,
  consumableItems = []
}) => {
  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeWarehouse, setActiveWarehouse] = useState('all'); // 'all' | 'main' | 'sub' | 'storage' | 'clinic'
  const [activeStatus, setActiveStatus] = useState('all'); // 'all' | 'low' | 'expiring' | 'normal'
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

  // Report Modal states
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportTargetWarehouse, setReportTargetWarehouse] = useState('คลังใหญ่ (คลังพัสดุ)');
  const [trackedQrItems, setTrackedQrItems] = useState(consumableItems || []);
  const [pendingTransfersCount, setPendingTransfersCount] = useState(0);

  // Load sub-warehouse transfers and consumable items
  useEffect(() => {
    let isMounted = true;
    const loadSubWarehouseMeta = async () => {
      // 1. Fetch pending transfers for notification badge
      try {
        const transfers = await fetchConsumableTransfers(supabase);
        if (isMounted && Array.isArray(transfers)) {
          const pending = transfers.filter((t) => t && t.status === 'pending_sub_receipt');
          setPendingTransfersCount(pending.length);
        }
      } catch (e) {
        // ignore fallback
      }

      // 2. Fetch consumable_items for QR badge
      try {
        if (Array.isArray(consumableItems) && consumableItems.length > 0) {
          if (isMounted) setTrackedQrItems(consumableItems);
          return;
        }
        if (supabase) {
          const { data, error } = await supabase
            .from('consumable_items')
            .select('*');
          if (!error && Array.isArray(data) && isMounted) {
            setTrackedQrItems(data);
          }
        }
      } catch (e) {
        // ignore fallback
      }
    };
    loadSubWarehouseMeta();
    return () => {
      isMounted = false;
    };
  }, [consumableItems]);

  // Index of QR tracked items
  const qrTrackedMap = useMemo(() => {
    const set = new Set();
    (trackedQrItems || []).forEach((item) => {
      if (!item) return;
      if (item.master_id) set.add(String(item.master_id));
      if (item.id) set.add(String(item.id));
      if (item.qr_code) set.add(String(item.qr_code));
      if (item.item_name && item.lot_number) {
        set.add(`${item.item_name}__${item.lot_number}`);
      }
      if (item.item_name) {
        set.add(`NAME__${item.item_name}`);
      }
    });
    return set;
  }, [trackedQrItems]);

  const isLotQrTracked = useCallback((lot, itemName) => {
    if (!lot) return false;
    if (lot.qr_code) return true;
    if (Array.isArray(lot.qr_codes) && lot.qr_codes.length > 0) return true;
    if (lot.id && qrTrackedMap.has(String(lot.id))) return true;
    if (lot.lot_number && itemName && qrTrackedMap.has(`${itemName}__${lot.lot_number}`)) return true;
    return false;
  }, [qrTrackedMap]);

  const isItemQrTracked = useCallback((item) => {
    if (!item) return false;
    if (qrTrackedMap.has(`NAME__${item.name}`)) return true;
    if (item.firstLot && isLotQrTracked(item.firstLot, item.name)) return true;
    if (Array.isArray(item.lotDetails) && item.lotDetails.some((l) => isLotQrTracked(l, item.name))) return true;
    if (Array.isArray(item.lots) && item.lots.some((l) => isLotQrTracked(l, item.name))) return true;
    return false;
  }, [qrTrackedMap, isLotQrTracked]);

  // Expandable UI states
  const [expandedLots, setExpandedLots] = useState({});
  const [expandedGallery, setExpandedGallery] = useState({});
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all' | 'transfer' | 'stockin'
  const [historySearch, setHistorySearch] = useState('');
  const [historyLimit, setHistoryLimit] = useState(15);

  // Safe image parsing wrapper
  const getImages = useCallback((imageUrlField) => {
    if (typeof parseImages === 'function') {
      try {
        const res = parseImages(imageUrlField);
        if (res && (res.full || res.zoom || res.other || res.document)) {
          return {
            full: res.full || '',
            zoom: res.zoom || '',
            other: res.other || '',
            document: res.document || ''
          };
        }
      } catch (e) {
        // fallback
      }
    }
    return defaultParseImages(imageUrlField);
  }, [parseImages]);

  // Safe status config wrapper
  const getLotStatus = useCallback((lotAmount, expDate) => {
    if (typeof getStatusConfig === 'function') {
      try {
        return getStatusConfig(lotAmount, expDate);
      } catch (e) {
        // fallback
      }
    }
    return defaultGetStatusConfig(lotAmount, expDate);
  }, [getStatusConfig]);

  // Filter only consumable items (category === 'วัสดุสิ้นเปลือง' && !m.is_set)
  const consumableMasters = useMemo(() => {
    return (itemMaster || []).filter(
      (m) => m && m.category === 'วัสดุสิ้นเปลือง' && !m.is_set
    );
  }, [itemMaster]);

  // Index supplies by master_id
  const suppliesByMaster = useMemo(() => {
    const map = new Map();
    (supplies || []).forEach((s) => {
      if (!s || s.master_id === undefined || s.master_id === null) return;
      if (!map.has(s.master_id)) {
        map.set(s.master_id, []);
      }
      map.get(s.master_id).push(s);
    });
    return map;
  }, [supplies]);

  // Group consumable lots by unique item_name and calculate all 4-warehouse distributions
  const groupedItems = useMemo(() => {
    const nameMap = new Map();

    consumableMasters.forEach((lot) => {
      const name = (lot.item_name || 'ไม่ระบุชื่อ').trim();
      if (!nameMap.has(name)) {
        nameMap.set(name, []);
      }
      nameMap.get(name).push(lot);
    });

    const items = [];

    nameMap.forEach((lots, name) => {
      const firstLot = lots[0] || {};
      const globalMinStock = Number(firstLot.global_min_stock) || 0;
      const unit = firstLot.unit || 'ชิ้น';
      const unitSize = firstLot.unit_size || '';
      const aliases = firstLot.aliases || '';

      const locationParts = [
        firstLot.cabinet ? `ตู้: ${firstLot.cabinet}` : '',
        firstLot.shelf ? `ชั้น: ${firstLot.shelf}` : '',
        firstLot.row ? `แถว: ${firstLot.row}` : '',
        firstLot.location ? `${firstLot.location}` : ''
      ].filter(Boolean);
      const locationText = locationParts.length > 0 ? locationParts.join(' ') : 'ไม่ระบุตำแหน่ง';

      let totalUsable = 0;
      let totalStockAllWh = 0;
      let mainQty = 0;
      let subQty = 0;
      let storageQty = 0;
      let clinicQty = 0;

      let hasExpired = false;
      let hasExpiringSoon = false;

      // Compute details per lot
      const lotDetails = lots.map((lot) => {
        const lotSupplies = suppliesByMaster.get(lot.id) || [];

        let lotMain = 0;
        let lotSub = 0;
        let lotStorage = 0;
        let lotClinic = 0;

        lotSupplies.forEach((s) => {
          const qty = Number(s.item_amount) || 0;
          const whKey = getWarehouseKey(s.warehouse_name);
          if (whKey === 'main') lotMain += qty;
          else if (whKey === 'sub') lotSub += qty;
          else if (whKey === 'storage') lotStorage += qty;
          else if (whKey === 'clinic') lotClinic += qty;
        });

        const lotTotal = lotMain + lotSub + lotStorage + lotClinic;
        const status = getLotStatus(lotTotal, lot.exp_date);

        const isExpired = status.label === 'หมดอายุ';
        const isExpiring = status.label === '< 3ด.' || status.label === '3ด.-1ปี';

        if (isExpired) {
          hasExpired = true;
        } else {
          totalUsable += lotTotal;
        }

        if (isExpiring) {
          hasExpiringSoon = true;
        }

        mainQty += lotMain;
        subQty += lotSub;
        storageQty += lotStorage;
        clinicQty += lotClinic;
        totalStockAllWh += lotTotal;

        return {
          id: lot.id,
          lot_number: lot.lot_number || '-',
          exp_date: lot.exp_date || '-',
          min_stock: lot.min_stock || 0,
          lotTotal,
          lotMain,
          lotSub,
          lotStorage,
          lotClinic,
          status,
          isExpired,
          isExpiring
        };
      });

      // Sort lots by expiration date (soonest first)
      lotDetails.sort((a, b) => {
        if (!a.exp_date || a.exp_date === '-') return 1;
        if (!b.exp_date || b.exp_date === '-') return -1;
        return new Date(a.exp_date) - new Date(b.exp_date);
      });

      const isLowStock = totalUsable < globalMinStock || totalUsable === 0;
      const isExpiringOrExpired = hasExpired || hasExpiringSoon;

      const images = getImages(firstLot.image_url);
      const thumbUrl = images.full || images.zoom || images.other || images.document || '';

      const galleryImages = [
        { label: '📸 รูปเต็ม', url: images.full, color: '#00bcd4' },
        { label: '🔍 รูปซูม', url: images.zoom, color: '#3b82f6' },
        { label: '🖼️ รูปอื่นๆ', url: images.other, color: '#8b5cf6' },
        { label: '📄 เอกสาร', url: images.document, color: '#f59e0b' }
      ].filter((img) => img.url);

      items.push({
        name,
        lots,
        firstLot,
        globalMinStock,
        unit,
        unitSize,
        aliases,
        locationText,
        lotDetails,
        totalUsable,
        totalStockAllWh,
        mainQty,
        subQty,
        storageQty,
        clinicQty,
        isLowStock,
        hasExpired,
        hasExpiringSoon,
        isExpiringOrExpired,
        images,
        thumbUrl,
        galleryImages
      });
    });

    // Sort alphabetically by item name
    items.sort((a, b) => a.name.localeCompare(b.name, 'th'));
    return items;
  }, [consumableMasters, suppliesByMaster, getLotStatus, getImages]);

  // Overall KPI Metrics Calculation
  const kpiMetrics = useMemo(() => {
    let totalSKUs = groupedItems.length;
    let totalUsableUnits = 0;

    let mainItemsCount = 0;
    let mainUnitsSum = 0;

    let subItemsCount = 0;
    let subUnitsSum = 0;

    let storageItemsCount = 0;
    let storageUnitsSum = 0;

    let clinicItemsCount = 0;
    let clinicUnitsSum = 0;

    let lowStockCount = 0;
    let expiringLotsCount = 0;
    let expiringItemsCount = 0;

    groupedItems.forEach((item) => {
      totalUsableUnits += item.totalUsable;

      if (item.mainQty > 0) {
        mainItemsCount += 1;
        mainUnitsSum += item.mainQty;
      }
      if (item.subQty > 0) {
        subItemsCount += 1;
        subUnitsSum += item.subQty;
      }
      if (item.storageQty > 0) {
        storageItemsCount += 1;
        storageUnitsSum += item.storageQty;
      }
      if (item.clinicQty > 0) {
        clinicItemsCount += 1;
        clinicUnitsSum += item.clinicQty;
      }

      if (item.isLowStock) {
        lowStockCount += 1;
      }

      if (item.isExpiringOrExpired) {
        expiringItemsCount += 1;
      }

      item.lotDetails.forEach((lot) => {
        if (lot.isExpired || lot.isExpiring) {
          expiringLotsCount += 1;
        }
      });
    });

    return {
      totalSKUs,
      totalUsableUnits,
      main: { items: mainItemsCount, units: mainUnitsSum },
      sub: { items: subItemsCount, units: subUnitsSum },
      storage: { items: storageItemsCount, units: storageUnitsSum },
      clinic: { items: clinicItemsCount, units: clinicUnitsSum },
      lowStockCount,
      expiringLotsCount,
      expiringItemsCount
    };
  }, [groupedItems]);

  // Filtered items based on Search, Warehouse filter, and Status filter
  const filteredItems = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();

    return groupedItems.filter((item) => {
      // 1. Search Query Filter
      if (q) {
        const matchName = item.name.toLowerCase().includes(q);
        const matchAlias = item.aliases && item.aliases.toLowerCase().includes(q);
        const matchLocation = item.locationText && item.locationText.toLowerCase().includes(q);
        const matchLot = item.lotDetails.some((l) => l.lot_number.toLowerCase().includes(q));
        if (!matchName && !matchAlias && !matchLocation && !matchLot) return false;
      }

      // 2. Warehouse Filter
      if (activeWarehouse === 'main' && item.mainQty <= 0) return false;
      if (activeWarehouse === 'sub' && item.subQty <= 0) return false;
      if (activeWarehouse === 'storage' && item.storageQty <= 0) return false;
      if (activeWarehouse === 'clinic' && item.clinicQty <= 0) return false;

      // 3. Status Filter
      if (activeStatus === 'low') {
        return item.isLowStock;
      }
      if (activeStatus === 'expiring') {
        return item.isExpiringOrExpired;
      }
      if (activeStatus === 'normal') {
        return !item.isLowStock && !item.isExpiringOrExpired;
      }

      return true;
    });
  }, [groupedItems, searchQuery, activeWarehouse, activeStatus]);

  // Consumable set of item names for movement logs filtering
  const consumableItemNames = useMemo(() => {
    return new Set(groupedItems.map((item) => item.name));
  }, [groupedItems]);

  // Recent Transfer and Movement Logs Filter
  const recentMovements = useMemo(() => {
    return (logs || [])
      .filter((log) => {
        if (!log || !log.action) return false;
        const act = String(log.action);
        const wh = String(log.warehouse || '');
        const isTransfer = act.includes('โอนย้าย') || act.includes('โอน');
        const isStockIn = act.includes('รับเข้าสินค้า') || act.includes('รับเข้า') || act.includes('ตรวจรับ');
        const isConsumable = consumableItemNames.has(log.item_name) || act.includes('สิ้นเปลือง');
        const isConsumableWarehouse =
          wh.includes('คลังใหญ่') || wh.includes('คลังย่อย') || wh.includes('ห้องเก็บของ') || wh.includes('ห้องคลีนิก');

        return isTransfer || isStockIn || (isConsumable && (isConsumableWarehouse || act.includes('เบิก') || act.includes('จ่าย')));
      })
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [logs, consumableItemNames]);

  // Filtered recent movements based on sub-filter and search
  const displayedMovements = useMemo(() => {
    const q = (historySearch || '').trim().toLowerCase();
    return recentMovements.filter((log) => {
      const act = String(log.action || '');
      if (historyFilter === 'transfer' && !act.includes('โอน')) return false;
      if (historyFilter === 'stockin' && !act.includes('รับเข้า') && !act.includes('ตรวจรับ')) return false;

      if (q) {
        const matchItem = log.item_name && log.item_name.toLowerCase().includes(q);
        const matchAction = act.toLowerCase().includes(q);
        const matchWh = log.warehouse && log.warehouse.toLowerCase().includes(q);
        const actor = getLogActor(log).toLowerCase();
        const matchActor = actor.includes(q);
        if (!matchItem && !matchAction && !matchWh && !matchActor) return false;
      }
      return true;
    });
  }, [recentMovements, historyFilter, historySearch]);

  // Toggle individual lot breakdown drawer
  const toggleLotDrawer = (itemName) => {
    setExpandedLots((prev) => ({ ...prev, [itemName]: !prev[itemName] }));
  };

  // Toggle gallery drawer
  const toggleGalleryDrawer = (itemName) => {
    setExpandedGallery((prev) => ({ ...prev, [itemName]: !prev[itemName] }));
  };

  // Expand / collapse all lots
  const allLotsExpanded = filteredItems.length > 0 && filteredItems.every((item) => expandedLots[item.name]);
  const toggleExpandAllLots = () => {
    if (allLotsExpanded) {
      setExpandedLots({});
    } else {
      const newState = {};
      filteredItems.forEach((item) => {
        newState[item.name] = true;
      });
      setExpandedLots(newState);
    }
  };

  return (
    <div className="consumable-dashboard-container" style={{ padding: isMobile ? '8px 4px' : '16px 8px' }}>
      {/* 1. Header Section */}
      <div
        className="consumable-header"
        style={{
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.7) 100%)',
          borderRadius: '20px',
          padding: isMobile ? '18px 14px' : '24px 28px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <h2
            style={{
              fontSize: isMobile ? '1.4em' : '1.85em',
              fontWeight: '800',
              margin: '0 0 6px 0',
              color: '#ffffff',
              letterSpacing: '0.3px',
              textShadow: '0 2px 14px rgba(0, 188, 212, 0.4)'
            }}
          >
            📊 แดชบอร์ดภาพรวมวัสดุสิ้นเปลือง (4 คลังหลัก)
          </h2>
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: isMobile ? '0.85em' : '0.98em',
              margin: '0 0 16px 0',
              maxWidth: '850px',
              lineHeight: '1.5'
            }}
          >
            ตรวจสอบการกระจายตัวของสต็อกใน คลังใหญ่, คลังย่อย, ห้องเก็บของ และ ห้องคลีนิก แบบเรียลไทม์
          </p>
          <div className="accent-bar" style={{ width: '80px', height: '3px', marginBottom: '18px' }} />

          {/* Quick Action Buttons */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            {/* Prominent Button: ศูนย์เอกสารรายงาน */}
            <button
              type="button"
              onClick={() => {
                setReportTargetWarehouse('คลังใหญ่ (คลังพัสดุ)');
                setIsReportModalOpen(true);
              }}
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
                color: '#ffffff',
                border: '1px solid rgba(52, 211, 153, 0.5)',
                borderRadius: '12px',
                padding: '10px 18px',
                fontSize: '0.9em',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(13, 148, 136, 0.4)',
                transition: 'all 0.25s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(13, 148, 136, 0.55)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(13, 148, 136, 0.4)';
              }}
            >
              <span>📑 ศูนย์เอกสารรายงาน (คลังใหญ่ / คลังย่อย)</span>
            </button>

            {/* Prominent Button: จัดการคลังย่อย */}
            <button
              type="button"
              onClick={() => onNavigate('sub-warehouse-admin')}
              style={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
                color: '#ffffff',
                border: '1px solid rgba(129, 140, 248, 0.5)',
                borderRadius: '12px',
                padding: '10px 18px',
                fontSize: '0.9em',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(79, 70, 229, 0.4)',
                transition: 'all 0.25s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(79, 70, 229, 0.55)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(79, 70, 229, 0.4)';
              }}
            >
              <span>🏬 จัดการคลังย่อย (ผู้ดูแลคลัง)</span>
              {pendingTransfersCount > 0 && (
                <span
                  style={{
                    background: '#ef4444',
                    color: '#ffffff',
                    fontSize: '0.75em',
                    fontWeight: '800',
                    padding: '2px 7px',
                    borderRadius: '9999px',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)'
                  }}
                  title={`มีรายการรอรับเข้า ${pendingTransfersCount} รายการ`}
                >
                  {pendingTransfersCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => onOpenTransfer()}
              style={{
                background: 'linear-gradient(135deg, #00bcd4 0%, #00838f 100%)',
                color: '#ffffff',
                border: '1px solid rgba(0, 229, 255, 0.4)',
                borderRadius: '12px',
                padding: '10px 18px',
                fontSize: '0.9em',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(0, 188, 212, 0.35)',
                transition: 'all 0.25s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 188, 212, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 188, 212, 0.35)';
              }}
            >
              <span>🔄 โอนย้ายสต็อกข้ามคลัง</span>
            </button>


            <button
              type="button"
              onClick={() => onNavigate('receipt-stockin')}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
                color: '#ffffff',
                border: '1px solid rgba(52, 211, 153, 0.4)',
                borderRadius: '12px',
                padding: '10px 18px',
                fontSize: '0.9em',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.25s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
              }}
            >
              <span>📥 รับเข้าตามใบเสร็จ</span>
            </button>

            <button
              type="button"
              onClick={() => onNavigate('dispense-report')}
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                color: '#ffffff',
                border: '1px solid rgba(52, 211, 153, 0.4)',
                borderRadius: '12px',
                padding: '10px 18px',
                fontSize: '0.9em',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(5, 150, 105, 0.3)',
                transition: 'all 0.25s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(5, 150, 105, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(5, 150, 105, 0.3)';
              }}
            >
              <span>📄 รายงานการเบิกจ่าย</span>
            </button>

            <button
              type="button"
              onClick={() => onNavigate('consumable')}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.16)',
                borderRadius: '12px',
                padding: '10px 18px',
                fontSize: '0.9em',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.25s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.14)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <span>📦 รายการสต็อก & ล็อต</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Top KPI Metrics (Summary Cards) */}
      <div
        className="consumable-stats-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: '12px'
        }}
      >
        {/* KPI: Overview Total SKUs & Usable Units */}
        <div
          className={`consumable-stat-card total ${activeWarehouse === 'all' && activeStatus === 'all' ? 'active' : ''}`}
          onClick={() => {
            setActiveWarehouse('all');
            setActiveStatus('all');
          }}
          title="คลิกเพื่อดูภาพรวมทั้งหมด (Matrix 4 คลัง)"
          style={{ cursor: 'pointer' }}
        >
          <div className="consumable-stat-icon" style={{ background: 'rgba(0, 188, 212, 0.15)', color: '#00e5ff' }}>
            📦
          </div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value" style={{ fontSize: isMobile ? '1.3em' : '1.65em' }}>
              {kpiMetrics.totalSKUs}
            </div>
            <div className="consumable-stat-label">
              <span>SKUs ทั้งหมด</span>
              <div style={{ fontSize: '0.85em', color: '#00e5ff', marginTop: '2px', fontWeight: '600' }}>
                พร้อมใช้ {kpiMetrics.totalUsableUnits.toLocaleString()} ชิ้น
              </div>
            </div>
          </div>
        </div>

        {/* KPI: Main Warehouse (คลังใหญ่) */}
        <div
          className={`consumable-stat-card ${activeWarehouse === 'main' ? 'active' : ''}`}
          onClick={() => setActiveWarehouse((prev) => (prev === 'main' ? 'all' : 'main'))}
          title="คลิกเพื่อกรองเฉพาะสินค้าที่มีใน คลังใหญ่ (คลังพัสดุ)"
          style={{
            cursor: 'pointer',
            borderColor: activeWarehouse === 'main' ? '#00bcd4' : 'rgba(255, 255, 255, 0.08)'
          }}
        >
          <div className="consumable-stat-icon" style={{ background: 'rgba(0, 188, 212, 0.15)', color: '#00bcd4' }}>
            🏢
          </div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value" style={{ fontSize: isMobile ? '1.3em' : '1.65em' }}>
              {kpiMetrics.main.units.toLocaleString()}
            </div>
            <div className="consumable-stat-label">
              <span>คลังใหญ่ (พัสดุ)</span>
              <div style={{ fontSize: '0.85em', color: '#67e8f9', marginTop: '2px' }}>
                {kpiMetrics.main.items} รายการ
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setReportTargetWarehouse('คลังใหญ่ (คลังพัสดุ)');
                  setIsReportModalOpen(true);
                }}
                style={{
                  marginTop: '6px',
                  background: 'rgba(0, 188, 212, 0.18)',
                  border: '1px solid rgba(0, 188, 212, 0.4)',
                  color: '#00e5ff',
                  borderRadius: '6px',
                  padding: '3px 8px',
                  fontSize: '0.75em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease'
                }}
                title="เปิดรายงานเฉพาะคลังใหญ่"
              >
                📄 รายงานคลังนี้
              </button>
            </div>
          </div>
        </div>

        {/* KPI: Sub Warehouse (คลังย่อย) */}
        <div
          className={`consumable-stat-card ${activeWarehouse === 'sub' ? 'active' : ''}`}
          onClick={() => setActiveWarehouse((prev) => (prev === 'sub' ? 'all' : 'sub'))}
          title="คลิกเพื่อกรองเฉพาะสินค้าที่มีใน คลังย่อย (คลังห้องฟัน)"
          style={{
            cursor: 'pointer',
            borderColor: activeWarehouse === 'sub' ? '#3b82f6' : 'rgba(255, 255, 255, 0.08)'
          }}
        >
          <div className="consumable-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
            🏬
          </div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value" style={{ fontSize: isMobile ? '1.3em' : '1.65em' }}>
              {kpiMetrics.sub.units.toLocaleString()}
            </div>
            <div className="consumable-stat-label">
              <span>คลังย่อย (ห้องฟัน)</span>
              <div style={{ fontSize: '0.85em', color: '#93c5fd', marginTop: '2px' }}>
                {kpiMetrics.sub.items} รายการ
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setReportTargetWarehouse('คลังย่อย (คลังห้องฟัน)');
                  setIsReportModalOpen(true);
                }}
                style={{
                  marginTop: '6px',
                  background: 'rgba(59, 130, 246, 0.18)',
                  border: '1px solid rgba(59, 130, 246, 0.4)',
                  color: '#60a5fa',
                  borderRadius: '6px',
                  padding: '3px 8px',
                  fontSize: '0.75em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease'
                }}
                title="เปิดรายงานเฉพาะคลังย่อย"
              >
                📄 รายงานคลังนี้
              </button>
            </div>
          </div>
        </div>

        {/* KPI: Storage Room (ห้องเก็บของ) */}
        <div
          className={`consumable-stat-card ${activeWarehouse === 'storage' ? 'active' : ''}`}
          onClick={() => setActiveWarehouse((prev) => (prev === 'storage' ? 'all' : 'storage'))}
          title="คลิกเพื่อกรองเฉพาะสินค้าที่มีใน ห้องเก็บของ"
          style={{
            cursor: 'pointer',
            borderColor: activeWarehouse === 'storage' ? '#f59e0b' : 'rgba(255, 255, 255, 0.08)'
          }}
        >
          <div className="consumable-stat-icon" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
            📦
          </div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value" style={{ fontSize: isMobile ? '1.3em' : '1.65em' }}>
              {kpiMetrics.storage.units.toLocaleString()}
            </div>
            <div className="consumable-stat-label">
              <span>ห้องเก็บของ</span>
              <div style={{ fontSize: '0.85em', color: '#fcd34d', marginTop: '2px' }}>
                {kpiMetrics.storage.items} รายการ
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setReportTargetWarehouse('ห้องเก็บของ');
                  setIsReportModalOpen(true);
                }}
                style={{
                  marginTop: '6px',
                  background: 'rgba(245, 158, 11, 0.18)',
                  border: '1px solid rgba(245, 158, 11, 0.4)',
                  color: '#fbbf24',
                  borderRadius: '6px',
                  padding: '3px 8px',
                  fontSize: '0.75em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="เปิดรายงานห้องเก็บของ"
              >
                📄 รายงานคลังนี้
              </button>
            </div>
          </div>
        </div>

        {/* KPI: Clinic Room (ห้องคลีนิก) */}
        <div
          className={`consumable-stat-card ${activeWarehouse === 'clinic' ? 'active' : ''}`}
          onClick={() => setActiveWarehouse((prev) => (prev === 'clinic' ? 'all' : 'clinic'))}
          title="คลิกเพื่อกรองเฉพาะสินค้าที่มีใน ห้องคลีนิก"
          style={{
            cursor: 'pointer',
            borderColor: activeWarehouse === 'clinic' ? '#10b981' : 'rgba(255, 255, 255, 0.08)'
          }}
        >
          <div className="consumable-stat-icon" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
            🏥
          </div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value" style={{ fontSize: isMobile ? '1.3em' : '1.65em' }}>
              {kpiMetrics.clinic.units.toLocaleString()}
            </div>
            <div className="consumable-stat-label">
              <span>ห้องคลีนิก</span>
              <div style={{ fontSize: '0.85em', color: '#6ee7b7', marginTop: '2px' }}>
                {kpiMetrics.clinic.items} รายการ
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setReportTargetWarehouse('ห้องคลีนิก');
                  setIsReportModalOpen(true);
                }}
                style={{
                  marginTop: '6px',
                  background: 'rgba(16, 185, 129, 0.18)',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  color: '#34d399',
                  borderRadius: '6px',
                  padding: '3px 8px',
                  fontSize: '0.75em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="เปิดรายงานห้องคลีนิก"
              >
                📄 รายงานคลังนี้
              </button>
            </div>
          </div>
        </div>

        {/* KPI: Low Stock Alert */}
        <div
          className={`consumable-stat-card warning ${activeStatus === 'low' ? 'active' : ''}`}
          onClick={() => setActiveStatus((prev) => (prev === 'low' ? 'all' : 'low'))}
          title="คลิกเพื่อกรองรายการสต็อกต่ำกว่าเกณฑ์ขั้นต่ำ"
          style={{ cursor: 'pointer' }}
        >
          <div className="consumable-stat-icon" style={{ background: 'rgba(243, 156, 18, 0.15)', color: '#f39c12' }}>
            ⚠️
          </div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value" style={{ fontSize: isMobile ? '1.3em' : '1.65em' }}>
              {kpiMetrics.lowStockCount}
            </div>
            <div className="consumable-stat-label">สต็อกต่ำกว่าเกณฑ์</div>
          </div>
        </div>

        {/* KPI: Expired / Expiring Soon */}
        <div
          className={`consumable-stat-card alert ${activeStatus === 'expiring' ? 'active' : ''}`}
          onClick={() => setActiveStatus((prev) => (prev === 'expiring' ? 'all' : 'expiring'))}
          title="คลิกเพื่อกรองล็อตที่หมดอายุ หรือใกล้หมดอายุ"
          style={{ cursor: 'pointer' }}
        >
          <div className="consumable-stat-icon" style={{ background: 'rgba(231, 76, 60, 0.15)', color: '#e74c3c' }}>
            ⏰
          </div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value" style={{ fontSize: isMobile ? '1.3em' : '1.65em' }}>
              {kpiMetrics.expiringLotsCount}
            </div>
            <div className="consumable-stat-label">
              <span>หมดอายุ/ใกล้หมด</span>
              <div style={{ fontSize: '0.8em', color: '#fca5a5', marginTop: '2px' }}>
                ({kpiMetrics.expiringItemsCount} รายการ)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Search & Filter Controls Bar */}
      <div
        className="consumable-controls-bar"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          padding: '14px 18px'
        }}
      >
        {/* Row 1: Search & View Toggle */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
          }}
        >
          <div className="consumable-search-wrapper" style={{ flex: '1 1 300px', maxWidth: '100%' }}>
            <span className="consumable-search-icon">🔍</span>
            <input
              type="text"
              className="consumable-search-input"
              placeholder="ค้นหาชื่อวัสดุสิ้นเปลือง, ชื่อเล่น (Aliases), ล็อต, ตู้/ชั้น..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="consumable-search-clear"
                onClick={() => setSearchQuery('')}
                title="ล้างการค้นหา"
              >
                ✕
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* View Mode Switcher */}
            <div className="consumable-view-toggle">
              <button
                type="button"
                className={`consumable-view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
                title="มุมมองแบบการ์ด"
              >
                🔲 การ์ด (Cards)
              </button>
              <button
                type="button"
                className={`consumable-view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="มุมมองตาราง Matrix 4 คลัง"
              >
                ☰ ตาราง (Matrix Table)
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: Warehouse Filter Tabs */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            paddingTop: '10px'
          }}
        >
          <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.5)', fontWeight: '600', marginRight: '4px' }}>
            คลัง:
          </span>

          <button
            type="button"
            className={`consumable-filter-btn ${activeWarehouse === 'all' ? 'active' : ''}`}
            onClick={() => setActiveWarehouse('all')}
          >
            🏢 ทั้งหมด (Matrix 4 คลัง)
            <span className="consumable-filter-count">{groupedItems.length}</span>
          </button>

          {WAREHOUSE_CONFIGS.map((wh) => (
            <button
              key={wh.key}
              type="button"
              className={`consumable-filter-btn ${activeWarehouse === wh.key ? 'active' : ''}`}
              onClick={() => setActiveWarehouse(wh.key)}
              style={{
                borderColor: activeWarehouse === wh.key ? wh.accentColor : 'rgba(255, 255, 255, 0.1)'
              }}
            >
              <span>{wh.icon} {wh.shortName}</span>
              <span className="consumable-filter-count">
                {kpiMetrics[wh.key]?.items || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Row 3: Status Filter Tabs */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            paddingTop: '10px'
          }}
        >
          <span style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.5)', fontWeight: '600', marginRight: '4px' }}>
            สถานะ:
          </span>

          <button
            type="button"
            className={`consumable-filter-btn ${activeStatus === 'all' ? 'active' : ''}`}
            onClick={() => setActiveStatus('all')}
          >
            ทั้งหมด
            <span className="consumable-filter-count">{groupedItems.length}</span>
          </button>

          <button
            type="button"
            className={`consumable-filter-btn ${activeStatus === 'low' ? 'active warning' : ''}`}
            onClick={() => setActiveStatus('low')}
          >
            ⚠️ สต็อกต่ำ
            <span className="consumable-filter-count">{kpiMetrics.lowStockCount}</span>
          </button>

          <button
            type="button"
            className={`consumable-filter-btn ${activeStatus === 'expiring' ? 'active alert' : ''}`}
            onClick={() => setActiveStatus('expiring')}
          >
            ⏰ ใกล้หมด/หมดอายุ
            <span className="consumable-filter-count">{kpiMetrics.expiringLotsCount}</span>
          </button>

          <button
            type="button"
            className={`consumable-filter-btn ${activeStatus === 'normal' ? 'active success' : ''}`}
            onClick={() => setActiveStatus('normal')}
          >
            ✅ สต็อกปกติ
          </button>
        </div>
      </div>

      {/* Sub-bar: Results count & Batch controls */}
      <div className="consumable-sub-bar" style={{ margin: '4px 0 8px 0' }}>
        <span>
          พบ <strong>{filteredItems.length}</strong> รายการ จากทั้งหมด {groupedItems.length} รายการ
          {activeWarehouse !== 'all' && ` (กรองตาม: ${WAREHOUSE_CONFIGS.find((w) => w.key === activeWarehouse)?.name || activeWarehouse})`}
        </span>
        <button
          type="button"
          className="consumable-expand-all-btn"
          onClick={toggleExpandAllLots}
        >
          {allLotsExpanded ? '▲ ยุบรายละเอียดทุกล็อต' : '▼ คลี่รายละเอียดทุกล็อต'}
        </button>
      </div>

      {/* 4. Multi-Warehouse Distribution Display (Cards vs Matrix Table) */}
      {filteredItems.length === 0 ? (
        <div className="consumable-empty-state">
          <div className="consumable-empty-icon">🔍</div>
          <div className="consumable-empty-title">ไม่พบรายการวัสดุสิ้นเปลืองที่ตรงกับเงื่อนไข</div>
          <div className="consumable-empty-desc">
            ลองปรับเปลี่ยนคำค้นหา หรือเลือกแท็บคลัง/สถานะใหม่อีกครั้ง
          </div>
          <button
            type="button"
            className="consumable-empty-reset-btn"
            onClick={() => {
              setSearchQuery('');
              setActiveWarehouse('all');
              setActiveStatus('all');
            }}
          >
            ↺ ล้างตัวกรองทั้งหมด
          </button>
        </div>
      ) : viewMode === 'cards' ? (
        /* CARDS VIEW */
        <div className="consumable-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(360px, 1fr))', gap: '16px' }}>
          {filteredItems.map((item) => {
            const isLotDrawerOpen = Boolean(expandedLots[item.name]);
            const isGalleryOpen = Boolean(expandedGallery[item.name]);

            return (
              <div
                key={item.name}
                className={`consumable-card ${item.isLowStock ? 'has-warning' : ''} ${item.hasExpired ? 'has-alert' : ''}`}
                style={{
                  background: 'rgba(255, 255, 255, 0.035)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                {/* Card Top: Thumbnail + Header Info */}
                <div className="consumable-card-top" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  {item.thumbUrl ? (
                    <div
                      className="consumable-thumb-box"
                      onClick={() => setExpandedImage(item.thumbUrl)}
                      title="คลิกเพื่อขยายรูปภาพ"
                    >
                      <img src={item.thumbUrl} alt={item.name} />
                    </div>
                  ) : (
                    <div className="consumable-thumb-placeholder">
                      <span className="placeholder-icon">📦</span>
                      <span>ไม่มีรูป</span>
                    </div>
                  )}

                  <div className="consumable-card-info" style={{ flex: 1, minWidth: 0 }}>
                    <div className="consumable-title-row" style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                      <span className="consumable-title" style={{ fontSize: '1.05em', fontWeight: '700', color: '#ffffff' }}>
                        {item.name}
                      </span>
                      {item.unitSize && (
                        <span className="consumable-unit-size">
                          {item.unitSize}
                        </span>
                      )}
                      {isItemQrTracked(item) && (
                        <span
                          className="consumable-qr-badge"
                          title="รายการนี้มีแท็ก QR Code ในระบบ consumable_items"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            background: 'rgba(16, 185, 129, 0.18)',
                            color: '#34d399',
                            border: '1px solid rgba(52, 211, 153, 0.4)',
                            borderRadius: '6px',
                            padding: '2px 7px',
                            fontSize: '0.72em',
                            fontWeight: '700'
                          }}
                        >
                          🏷️ QR Tagged
                        </span>
                      )}
                    </div>

                    {item.aliases && (
                      <div style={{ fontSize: '0.78em', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                        ชื่ออื่น: {item.aliases}
                      </div>
                    )}

                    {/* Location Badge */}
                    <div className="consumable-badges-row" style={{ marginTop: '4px' }}>
                      <span className="consumable-location-badge">
                        📍 {item.locationText}
                      </span>
                    </div>

                    {/* Total Usable vs Global Min Stock Badge */}
                    <div className="consumable-badges-row" style={{ marginTop: '4px' }}>
                      <span className={`consumable-stock-badge ${item.totalUsable < item.globalMinStock || item.totalUsable === 0 ? 'low' : 'normal'}`}>
                        {item.totalUsable < item.globalMinStock || item.totalUsable === 0 ? '⚠️' : '✓'} พร้อมใช้: {item.totalUsable.toLocaleString()} / ขั้นต่ำ: {item.globalMinStock} {item.unit}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 4-Warehouse Stock Badges Grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '8px',
                    background: 'rgba(0, 0, 0, 0.22)',
                    padding: '10px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  {WAREHOUSE_CONFIGS.map((wh) => {
                    const qty = item[`${wh.key}Qty`] || 0;
                    const isTargetWh = activeWarehouse === wh.key;

                    return (
                      <div
                        key={wh.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '6px 8px',
                          borderRadius: '8px',
                          background: isTargetWh ? wh.bgLight : qty > 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.01)',
                          border: `1px solid ${isTargetWh ? wh.accentColor : qty > 0 ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.03)'}`,
                          opacity: qty > 0 ? 1 : 0.6
                        }}
                      >
                        <span style={{ fontSize: '0.8em', color: 'rgba(255, 255, 255, 0.75)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <span>{wh.icon}</span> {wh.shortName}:
                        </span>
                        <span
                          style={{
                            fontSize: '0.9em',
                            fontWeight: '700',
                            color: qty > 0 ? wh.accentColor : 'rgba(255, 255, 255, 0.4)'
                          }}
                        >
                          {qty.toLocaleString()} <span style={{ fontSize: '0.78em', fontWeight: 'normal' }}>{item.unit}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Expiry Status Dots Summary */}
                {item.lotDetails.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '0.8em', color: 'rgba(255, 255, 255, 0.6)' }}>
                    <span>สถานะทุกล็อต ({item.lotDetails.length}):</span>
                    {item.lotDetails.map((lot, idx) => (
                      <span
                        key={lot.id || idx}
                        className="consumable-dot"
                        style={{ backgroundColor: lot.status.bg }}
                        title={`Lot: ${lot.lot_number} | EXP: ${lot.exp_date} | จำนวน: ${lot.lotTotal} | สถานะ: ${lot.status.label}`}
                      />
                    ))}
                  </div>
                )}

                {/* Gallery Drawer (if multiple images) */}
                {isGalleryOpen && item.galleryImages.length > 0 && (
                  <div className="consumable-gallery-drawer" style={{ marginTop: '4px' }}>
                    {item.galleryImages.map((img, gIdx) => (
                      <div key={gIdx} className="consumable-gallery-item">
                        <div
                          className="consumable-gallery-thumb"
                          onClick={() => setExpandedImage(img.url)}
                          title={`ขยาย ${img.label}`}
                        >
                          <img src={img.url} alt={img.label} />
                        </div>
                        <span className="consumable-gallery-label" style={{ color: img.color }}>
                          {img.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Lot Breakdown Drawer */}
                {isLotDrawerOpen && (
                  <div
                    className="consumable-lot-drawer"
                    style={{
                      marginTop: '4px',
                      padding: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}
                  >
                    <div style={{ fontSize: '0.82em', fontWeight: '700', color: '#00e5ff', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '4px' }}>
                      📋 รายละเอียดล็อตทั้งหมด ({item.lotDetails.length} ล็อต):
                    </div>
                    {item.lotDetails.map((lot, lIdx) => (
                      <div
                        key={lot.id || lIdx}
                        style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          borderRadius: '8px',
                          padding: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '0.84em', fontWeight: '600', color: '#ffffff' }}>
                              Lot: <code style={{ color: '#00e5ff' }}>{lot.lot_number}</code>
                            </span>
                            {isLotQrTracked(lot, item.name) && (
                              <span
                                style={{
                                  fontSize: '0.7em',
                                  padding: '1px 6px',
                                  borderRadius: '5px',
                                  backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                  color: '#34d399',
                                  border: '1px solid rgba(52, 211, 153, 0.35)',
                                  fontWeight: '700'
                                }}
                                title="ล็อตนี้มีรายการติดแท็ก QR Code ใน consumable_items"
                              >
                                🏷️ QR Code
                              </span>
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: '0.75em',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              backgroundColor: lot.status.bg,
                              color: lot.status.text || '#ffffff',
                              fontWeight: '700'
                            }}
                          >
                            {lot.status.label} (EXP: {lot.exp_date})
                          </span>
                        </div>

                        {/* Breakdown per warehouse for this lot */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '0.78em', color: 'rgba(255, 255, 255, 0.7)' }}>
                          <span>🏢 พัสดุ: <strong style={{ color: '#00bcd4' }}>{lot.lotMain}</strong></span>
                          <span>•</span>
                          <span>🏬 ห้องฟัน: <strong style={{ color: '#3b82f6' }}>{lot.lotSub}</strong></span>
                          <span>•</span>
                          <span>📦 เก็บของ: <strong style={{ color: '#f59e0b' }}>{lot.lotStorage}</strong></span>
                          <span>•</span>
                          <span>🏥 คลีนิก: <strong style={{ color: '#10b981' }}>{lot.lotClinic}</strong></span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '2px' }}>
                          <button
                            type="button"
                            onClick={() =>
                              onNavigate && onNavigate('dispense-report', { mode: 'ledger', itemId: lot.id || item.firstLot?.id || item.lots?.[0]?.id })
                            }
                            style={{
                              background: 'rgba(16, 185, 129, 0.15)',
                              border: '1px solid rgba(16, 185, 129, 0.3)',
                              color: '#34d399',
                              borderRadius: '6px',
                              padding: '3px 10px',
                              fontSize: '0.75em',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                            title="ดูบัญชีวัสดุล็อตนี้"
                          >
                            📑 บัญชีวัสดุ
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              onOpenTransfer({
                                masterId: lot.id,
                                itemName: item.name,
                                lotNumber: lot.lot_number
                              })
                            }
                            style={{
                              background: 'rgba(0, 188, 212, 0.15)',
                              border: '1px solid rgba(0, 188, 212, 0.3)',
                              color: '#00e5ff',
                              borderRadius: '6px',
                              padding: '3px 10px',
                              fontSize: '0.75em',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            🔄 โอนล็อตนี้
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Card Actions Footer */}
                <div className="consumable-card-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: 'auto', paddingTop: '8px' }}>
                  <button
                    type="button"
                    onClick={() =>
                      onOpenTransfer({
                        masterId: item.firstLot?.id,
                        itemName: item.name,
                        lotNumber: item.firstLot?.lot_number || ''
                      })
                    }
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, rgba(0, 188, 212, 0.2) 0%, rgba(0, 150, 136, 0.25) 100%)',
                      border: '1px solid rgba(0, 229, 255, 0.4)',
                      color: '#ffffff',
                      padding: '8px 14px',
                      borderRadius: '10px',
                      fontSize: '0.85em',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0, 188, 212, 0.35)')}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        'linear-gradient(135deg, rgba(0, 188, 212, 0.2) 0%, rgba(0, 150, 136, 0.25) 100%)')
                    }
                  >
                    <span>🔄 โอนย้าย</span>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      onNavigate && onNavigate('dispense-report', { mode: 'ledger', itemId: item.firstLot?.id || item.lots?.[0]?.id })
                    }
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.25) 100%)',
                      border: '1px solid rgba(52, 211, 153, 0.4)',
                      color: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '10px',
                      fontSize: '0.85em',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(16, 185, 129, 0.35)')}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.25) 100%)')
                    }
                    title="ดูบัญชีวัสดุตามระเบียบพัสดุ"
                  >
                    <span>📑 บัญชีวัสดุ</span>
                  </button>

                  <button
                    type="button"
                    className={`consumable-expand-btn ${isLotDrawerOpen ? 'active' : ''}`}
                    onClick={() => toggleLotDrawer(item.name)}
                    style={{ flex: 1 }}
                  >
                    <span>📋 ล็อต ({item.lotDetails.length})</span>
                    <span>{isLotDrawerOpen ? '▲' : '▼'}</span>
                  </button>

                  {item.galleryImages.length > 0 && (
                    <button
                      type="button"
                      className={`consumable-preview-btn ${isGalleryOpen ? 'active' : ''}`}
                      onClick={() => toggleGalleryDrawer(item.name)}
                      title="ดูรูปภาพเพิ่มเติม"
                    >
                      🖼️ {item.galleryImages.length}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* MATRIX TABLE VIEW */
        <div
          className="stock-table-container"
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            overflowX: 'auto',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.88em',
              textAlign: 'left'
            }}
          >
            <thead>
              <tr
                style={{
                  background: 'rgba(0, 0, 0, 0.4)',
                  borderBottom: '2px solid rgba(0, 188, 212, 0.3)',
                  color: 'rgba(255, 255, 255, 0.85)'
                }}
              >
                <th style={{ padding: '12px 10px', width: '60px', textAlign: 'center' }}>รูป</th>
                <th style={{ padding: '12px 14px', minWidth: '180px' }}>ชื่อวัสดุ & ข้อมูล</th>
                <th style={{ padding: '12px 10px', textAlign: 'center', color: '#00bcd4', minWidth: '90px' }}>
                  🏢 คลังใหญ่
                </th>
                <th style={{ padding: '12px 10px', textAlign: 'center', color: '#3b82f6', minWidth: '90px' }}>
                  🏬 คลังย่อย
                </th>
                <th style={{ padding: '12px 10px', textAlign: 'center', color: '#f59e0b', minWidth: '90px' }}>
                  📦 ห้องเก็บของ
                </th>
                <th style={{ padding: '12px 10px', textAlign: 'center', color: '#10b981', minWidth: '90px' }}>
                  🏥 ห้องคลีนิก
                </th>
                <th style={{ padding: '12px 12px', textAlign: 'center', minWidth: '120px' }}>
                  รวมพร้อมใช้ / ขั้นต่ำ
                </th>
                <th style={{ padding: '12px 12px', textAlign: 'center', minWidth: '130px' }}>
                  สถานะล็อต & วันหมดอายุ
                </th>
                <th style={{ padding: '12px 12px', textAlign: 'center', minWidth: '130px' }}>
                  จัดการ
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const isLotDrawerOpen = Boolean(expandedLots[item.name]);

                return (
                  <React.Fragment key={item.name}>
                    <tr
                      style={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        background: item.isLowStock
                          ? 'rgba(243, 156, 18, 0.04)'
                          : item.hasExpired
                          ? 'rgba(231, 76, 60, 0.04)'
                          : 'transparent',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)')}
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = item.isLowStock
                          ? 'rgba(243, 156, 18, 0.04)'
                          : item.hasExpired
                          ? 'rgba(231, 76, 60, 0.04)'
                          : 'transparent')
                      }
                    >
                      {/* Image Thumbnail */}
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        {item.thumbUrl ? (
                          <div
                            style={{
                              width: '44px',
                              height: '44px',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              margin: '0 auto',
                              cursor: 'pointer',
                              background: '#000',
                              border: '1px solid rgba(255, 255, 255, 0.15)'
                            }}
                            onClick={() => setExpandedImage(item.thumbUrl)}
                            title="คลิกเพื่อขยายภาพ"
                          >
                            <img
                              src={item.thumbUrl}
                              alt={item.name}
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              width: '44px',
                              height: '44px',
                              borderRadius: '8px',
                              background: 'rgba(255, 255, 255, 0.04)',
                              border: '1px dashed rgba(255, 255, 255, 0.15)',
                              margin: '0 auto',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.2em'
                            }}
                          >
                            📦
                          </div>
                        )}
                      </td>

                      {/* Item Name & Details */}
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: '700', color: '#ffffff', fontSize: '0.98em' }}>
                            {item.name}
                          </span>
                          {isItemQrTracked(item) && (
                            <span
                              className="consumable-qr-badge"
                              title="รายการนี้มีแท็ก QR Code ในระบบ consumable_items"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                background: 'rgba(16, 185, 129, 0.18)',
                                color: '#34d399',
                                border: '1px solid rgba(52, 211, 153, 0.4)',
                                borderRadius: '6px',
                                padding: '2px 7px',
                                fontSize: '0.72em',
                                fontWeight: '700'
                              }}
                            >
                              🏷️ QR Tag
                            </span>
                          )}
                        </div>
                        {item.aliases && (
                          <div style={{ fontSize: '0.8em', color: 'rgba(255, 255, 255, 0.5)' }}>
                            {item.aliases}
                          </div>
                        )}
                        <div style={{ fontSize: '0.78em', color: '#cbd5e1', marginTop: '3px' }}>
                          📍 {item.locationText}
                        </div>
                      </td>

                      {/* 4 Warehouses Columns */}
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <span
                          style={{
                            fontWeight: item.mainQty > 0 ? '700' : 'normal',
                            color: item.mainQty > 0 ? '#00bcd4' : 'rgba(255, 255, 255, 0.3)',
                            fontSize: '1em'
                          }}
                        >
                          {item.mainQty.toLocaleString()}
                        </span>
                        <div style={{ fontSize: '0.75em', color: 'rgba(255, 255, 255, 0.4)' }}>{item.unit}</div>
                      </td>

                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <span
                          style={{
                            fontWeight: item.subQty > 0 ? '700' : 'normal',
                            color: item.subQty > 0 ? '#3b82f6' : 'rgba(255, 255, 255, 0.3)',
                            fontSize: '1em'
                          }}
                        >
                          {item.subQty.toLocaleString()}
                        </span>
                        <div style={{ fontSize: '0.75em', color: 'rgba(255, 255, 255, 0.4)' }}>{item.unit}</div>
                      </td>

                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <span
                          style={{
                            fontWeight: item.storageQty > 0 ? '700' : 'normal',
                            color: item.storageQty > 0 ? '#f59e0b' : 'rgba(255, 255, 255, 0.3)',
                            fontSize: '1em'
                          }}
                        >
                          {item.storageQty.toLocaleString()}
                        </span>
                        <div style={{ fontSize: '0.75em', color: 'rgba(255, 255, 255, 0.4)' }}>{item.unit}</div>
                      </td>

                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <span
                          style={{
                            fontWeight: item.clinicQty > 0 ? '700' : 'normal',
                            color: item.clinicQty > 0 ? '#10b981' : 'rgba(255, 255, 255, 0.3)',
                            fontSize: '1em'
                          }}
                        >
                          {item.clinicQty.toLocaleString()}
                        </span>
                        <div style={{ fontSize: '0.75em', color: 'rgba(255, 255, 255, 0.4)' }}>{item.unit}</div>
                      </td>

                      {/* Usable / Min Stock */}
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '3px 8px',
                            borderRadius: '8px',
                            fontSize: '0.85em',
                            fontWeight: '700',
                            backgroundColor:
                              item.totalUsable < item.globalMinStock || item.totalUsable === 0
                                ? 'rgba(231, 76, 60, 0.18)'
                                : 'rgba(46, 204, 113, 0.18)',
                            color:
                              item.totalUsable < item.globalMinStock || item.totalUsable === 0
                                ? '#ff6b6b'
                                : '#2ecc71',
                            border: `1px solid ${
                              item.totalUsable < item.globalMinStock || item.totalUsable === 0
                                ? 'rgba(231, 76, 60, 0.3)'
                                : 'rgba(46, 204, 113, 0.3)'
                            }`
                          }}
                        >
                          {item.totalUsable.toLocaleString()} / {item.globalMinStock} {item.unit}
                        </span>
                      </td>

                      {/* Lots and Expiry Dots */}
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
                          {item.lotDetails.map((lot, idx) => (
                            <span
                              key={lot.id || idx}
                              className="consumable-dot"
                              style={{ backgroundColor: lot.status.bg }}
                              title={`Lot: ${lot.lot_number} | EXP: ${lot.exp_date} | สถานะ: ${lot.status.label}`}
                            />
                          ))}
                        </div>
                        <div style={{ fontSize: '0.75em', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                          {item.lotDetails.length} ล็อต
                        </div>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            type="button"
                            onClick={() =>
                              onOpenTransfer({
                                masterId: item.firstLot?.id,
                                itemName: item.name,
                                lotNumber: item.firstLot?.lot_number || ''
                              })
                            }
                            style={{
                              background: 'rgba(0, 188, 212, 0.18)',
                              border: '1px solid rgba(0, 188, 212, 0.4)',
                              color: '#00e5ff',
                              borderRadius: '8px',
                              padding: '5px 10px',
                              fontSize: '0.82em',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                            title="โอนย้ายสต็อก"
                          >
                            🔄 โอนย้าย
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              onNavigate && onNavigate('dispense-report', { mode: 'ledger', itemId: item.firstLot?.id || item.lots?.[0]?.id })
                            }
                            style={{
                              background: 'rgba(16, 185, 129, 0.18)',
                              border: '1px solid rgba(16, 185, 129, 0.4)',
                              color: '#34d399',
                              borderRadius: '8px',
                              padding: '5px 10px',
                              fontSize: '0.82em',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                            title="ดูบัญชีวัสดุ"
                          >
                            📑 บัญชีวัสดุ
                          </button>

                          <button
                            type="button"
                            onClick={() => toggleLotDrawer(item.name)}
                            style={{
                              background: 'rgba(255, 255, 255, 0.06)',
                              border: '1px solid rgba(255, 255, 255, 0.12)',
                              color: 'rgba(255, 255, 255, 0.8)',
                              borderRadius: '8px',
                              padding: '5px 8px',
                              fontSize: '0.82em',
                              cursor: 'pointer'
                            }}
                            title="คลี่ดูรายละเอียดล็อต"
                          >
                            {isLotDrawerOpen ? '▲' : '▼'}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Sub-row when lots are expanded in Table view */}
                    {isLotDrawerOpen && (
                      <tr style={{ background: 'rgba(0, 0, 0, 0.3)' }}>
                        <td colSpan="9" style={{ padding: '12px 18px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ fontSize: '0.85em', fontWeight: '700', color: '#00e5ff' }}>
                              📋 รายละเอียดแยกล็อตของ: {item.name}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: '10px' }}>
                              {item.lotDetails.map((lot, lIdx) => (
                                <div
                                  key={lot.id || lIdx}
                                  style={{
                                    background: 'rgba(255, 255, 255, 0.03)',
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    borderRadius: '10px',
                                    padding: '10px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px'
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ fontWeight: '700', color: '#ffffff' }}>
                                        Lot: <code style={{ color: '#67e8f9' }}>{lot.lot_number}</code>
                                      </span>
                                      {isLotQrTracked(lot, item.name) && (
                                        <span
                                          style={{
                                            fontSize: '0.7em',
                                            padding: '1px 6px',
                                            borderRadius: '5px',
                                            backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                            color: '#34d399',
                                            border: '1px solid rgba(52, 211, 153, 0.35)',
                                            fontWeight: '700'
                                          }}
                                          title="ล็อตนี้มีรายการติดแท็ก QR Code ใน consumable_items"
                                        >
                                          🏷️ QR Code
                                        </span>
                                      )}
                                    </div>
                                    <span
                                      style={{
                                        fontSize: '0.75em',
                                        padding: '2px 8px',
                                        borderRadius: '8px',
                                        backgroundColor: lot.status.bg,
                                        color: lot.status.text || '#fff',
                                        fontWeight: '700'
                                      }}
                                    >
                                      {lot.status.label}
                                    </span>
                                  </div>

                                  <div style={{ fontSize: '0.78em', color: 'rgba(255, 255, 255, 0.6)' }}>
                                    วันหมดอายุ (EXP): <strong>{lot.exp_date}</strong>
                                  </div>

                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '0.8em', marginTop: '2px' }}>
                                    <span>🏢 {lot.lotMain}</span>
                                    <span>🏬 {lot.lotSub}</span>
                                    <span>📦 {lot.lotStorage}</span>
                                    <span>🏥 {lot.lotClinic}</span>
                                    <span style={{ fontWeight: '700', color: '#00e5ff' }}>= {lot.lotTotal} {item.unit}</span>
                                  </div>

                                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '4px' }}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        onNavigate && onNavigate('dispense-report', { mode: 'ledger', itemId: lot.id || item.firstLot?.id || item.lots?.[0]?.id })
                                      }
                                      style={{
                                        background: 'rgba(16, 185, 129, 0.2)',
                                        border: '1px solid rgba(16, 185, 129, 0.4)',
                                        color: '#34d399',
                                        borderRadius: '6px',
                                        padding: '4px 10px',
                                        fontSize: '0.78em',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                      }}
                                      title="ดูบัญชีวัสดุล็อตนี้"
                                    >
                                      📑 บัญชีวัสดุ
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        onOpenTransfer({
                                          masterId: lot.id,
                                          itemName: item.name,
                                          lotNumber: lot.lot_number
                                        })
                                      }
                                      style={{
                                        background: 'rgba(0, 188, 212, 0.2)',
                                        border: '1px solid rgba(0, 188, 212, 0.4)',
                                        color: '#00e5ff',
                                        borderRadius: '6px',
                                        padding: '4px 10px',
                                        fontSize: '0.78em',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      🔄 โอนล็อตนี้
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 5. Recent Transfer & Movement History Drawer/Section */}
      <div
        style={{
          marginTop: '20px',
          background: 'rgba(15, 23, 42, 0.7)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '18px',
          overflow: 'hidden',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)'
        }}
      >
        {/* Toggle Section Header */}
        <div
          onClick={() => setIsHistoryOpen((prev) => !prev)}
          style={{
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            background: isHistoryOpen ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
            borderBottom: isHistoryOpen ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
            userSelect: 'none',
            transition: 'background 0.2s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '1.4em' }}>📋</span>
            <div>
              <div style={{ fontWeight: '700', fontSize: '1.05em', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>ประวัติการโอนย้ายและรับเข้าสินค้าล่าสุด</span>
                <span
                  style={{
                    fontSize: '0.75em',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    background: 'rgba(0, 188, 212, 0.2)',
                    color: '#00e5ff',
                    border: '1px solid rgba(0, 188, 212, 0.35)'
                  }}
                >
                  {recentMovements.length} รายการ
                </span>
              </div>
              <div style={{ fontSize: '0.82em', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                Audit Trail บันทึกการเคลื่อนไหวสต็อกวัสดุสิ้นเปลือง
              </div>
            </div>
          </div>

          <div
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.06)',
              color: '#00e5ff',
              fontSize: '0.85em',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>{isHistoryOpen ? 'ซ่อนประวัติ' : 'ดูประวัติ'}</span>
            <span>{isHistoryOpen ? '▲' : '▼'}</span>
          </div>
        </div>

        {/* Collapsible Content */}
        {isHistoryOpen && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Movement Controls: Filter chips + Search */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setHistoryFilter('all')}
                  className={`consumable-filter-btn ${historyFilter === 'all' ? 'active' : ''}`}
                  style={{ padding: '6px 12px', fontSize: '0.82em' }}
                >
                  ทั้งหมด ({recentMovements.length})
                </button>
                <button
                  type="button"
                  onClick={() => setHistoryFilter('transfer')}
                  className={`consumable-filter-btn ${historyFilter === 'transfer' ? 'active' : ''}`}
                  style={{ padding: '6px 12px', fontSize: '0.82em' }}
                >
                  🔄 เฉพาะโอนย้าย
                </button>
                <button
                  type="button"
                  onClick={() => setHistoryFilter('stockin')}
                  className={`consumable-filter-btn ${historyFilter === 'stockin' ? 'active' : ''}`}
                  style={{ padding: '6px 12px', fontSize: '0.82em' }}
                >
                  📥 เฉพาะรับเข้า
                </button>
              </div>

              <div style={{ position: 'relative', width: isMobile ? '100%' : '260px' }}>
                <input
                  type="text"
                  placeholder="ค้นหาประวัติ..."
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    fontSize: '0.84em',
                    outline: 'none'
                  }}
                />
                {historySearch && (
                  <button
                    type="button"
                    onClick={() => setHistorySearch('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255,255,255,0.6)',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Movement Logs List / Table */}
            {displayedMovements.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '36px 16px',
                  color: 'rgba(255, 255, 255, 0.45)',
                  fontSize: '0.9em'
                }}
              >
                ไม่มีประวัติการเคลื่อนไหวที่ตรงกับเงื่อนไข
              </div>
            ) : (
              <div
                style={{
                  overflowX: 'auto',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.06)'
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.86em',
                    textAlign: 'left'
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: 'rgba(0, 0, 0, 0.35)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: 'rgba(255, 255, 255, 0.7)'
                      }}
                    >
                      <th style={{ padding: '10px 14px', width: '150px' }}>🕒 เวลา</th>
                      <th style={{ padding: '10px 14px', width: '120px' }}>📍 คลัง</th>
                      <th style={{ padding: '10px 14px', width: '180px' }}>📦 ชื่อวัสดุ</th>
                      <th style={{ padding: '10px 14px' }}>⚡ การดำเนินการ</th>
                      <th style={{ padding: '10px 14px', width: '120px', textAlign: 'center' }}>👤 ผู้ดำเนินการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedMovements.slice(0, historyLimit).map((log, lIdx) => {
                      const actor = getLogActor(log);
                      const isTransfer = (log.action || '').includes('โอน');
                      const isStockIn = (log.action || '').includes('รับเข้า');

                      return (
                        <tr
                          key={log.id || `${log.timestamp}-${lIdx}`}
                          style={{
                            borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                            background: lIdx % 2 === 0 ? 'rgba(255, 255, 255, 0.015)' : 'transparent'
                          }}
                        >
                          <td style={{ padding: '10px 14px', color: 'rgba(255, 255, 255, 0.75)', whiteSpace: 'nowrap' }}>
                            {formatThaiTimestamp(log.timestamp)}
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            <span
                              style={{
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.8em',
                                background: 'rgba(255, 255, 255, 0.06)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                color: '#cbd5e1'
                              }}
                            >
                              {log.warehouse || 'ส่วนกลาง'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px', fontWeight: '600', color: '#ffffff' }}>
                            {log.item_name || '-'}
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              color: isTransfer ? '#67e8f9' : isStockIn ? '#86efac' : 'rgba(255, 255, 255, 0.85)'
                            }}
                          >
                            {log.action}
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: '#fcd34d', fontWeight: '500' }}>
                            {actor}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {displayedMovements.length > historyLimit && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => setHistoryLimit((prev) => prev + 25)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    color: '#00e5ff',
                    borderRadius: '10px',
                    padding: '8px 20px',
                    fontSize: '0.85em',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  แสดงประวัติเพิ่มเติม (อีก {displayedMovements.length - historyLimit} รายการ)
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 5. Consumable Warehouse Report Modal */}
      <ConsumableWarehouseReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        itemMaster={itemMaster}
        supplies={supplies}
        logs={logs}
        initialWarehouse={reportTargetWarehouse}
        user={user}
        consumableItems={trackedQrItems}
      />
    </div>
  );
};

export default ConsumableDashboardPage;
